import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface CleaningPreferences {
  remove_duplicates: boolean;
  handle_missing_values: boolean;
  standardize_formats: boolean;
  trim_whitespace: boolean;
  normalize_case: boolean;
  remove_outliers: boolean;
  validate_emails: boolean;
  validate_phones: boolean;
  missing_value_strategy: "flag" | "most_common" | "mean" | "drop";
}

export const DEFAULT_CLEANING_PREFERENCES: CleaningPreferences = {
  remove_duplicates: true,
  handle_missing_values: true,
  standardize_formats: true,
  trim_whitespace: true,
  normalize_case: false,
  remove_outliers: false,
  validate_emails: true,
  validate_phones: false,
  missing_value_strategy: "flag",
};

/**
 * Cleaning preferences are persisted per user in `cleaning_preferences` and
 * read by the cleaning engine (src/lib/cleaning-engine.ts), so toggling a
 * switch changes what the cleaner actually does.
 */
export const useCleaningPreferences = () => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["cleaning-preferences"],
    queryFn: async (): Promise<CleaningPreferences> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return DEFAULT_CLEANING_PREFERENCES;

      const { data: row, error } = await supabase
        .from("cleaning_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      if (!row) return DEFAULT_CLEANING_PREFERENCES;

      return {
        remove_duplicates: row.remove_duplicates,
        handle_missing_values: row.handle_missing_values,
        standardize_formats: row.standardize_formats,
        trim_whitespace: row.trim_whitespace,
        normalize_case: row.normalize_case,
        remove_outliers: row.remove_outliers,
        validate_emails: row.validate_emails,
        validate_phones: row.validate_phones,
        missing_value_strategy: (row.missing_value_strategy ||
          "flag") as CleaningPreferences["missing_value_strategy"],
      };
    },
  });

  const { mutateAsync: savePreferences, isPending: isSaving } = useMutation({
    mutationFn: async (next: Partial<CleaningPreferences>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Please sign in to save cleaning preferences.");

      const merged = { ...(data ?? DEFAULT_CLEANING_PREFERENCES), ...next };
      const { error } = await supabase
        .from("cleaning_preferences")
        .upsert({ user_id: user.id, ...merged }, { onConflict: "user_id" });
      if (error) throw error;
      return merged;
    },
    onSuccess: (merged) => {
      queryClient.setQueryData(["cleaning-preferences"], merged);
    },
    onError: (e: any) => toast.error(e?.message || "Could not save preferences"),
  });

  return {
    preferences: data ?? DEFAULT_CLEANING_PREFERENCES,
    isLoading,
    isSaving,
    savePreferences,
  };
};
